import React, { useState, useRef, useEffect } from "react";

interface InfoTipProps {
  message: string;
  title?: string;
  icon?: "info" | "warning" | "tip" | "question";
  id?: string;
  className?: string;
  size?: "sm" | "md";
}

const ICONS: Record<string, React.ReactNode> = {
  info: (
    <svg className="h-full w-full" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  ),
  warning: (
    <svg className="h-full w-full" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  tip: (
    <svg className="h-full w-full" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.001z" />
    </svg>
  ),
  question: (
    <svg className="h-full w-full" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
  ),
};

const ICON_COLORS: Record<string, string> = {
  info: "text-blue-500",
  warning: "text-amber-500",
  tip: "text-brand",
  question: "text-ink-muted",
};

const InfoTip: React.FC<InfoTipProps> = ({
  message,
  title,
  icon = "info",
  id,
  className = "",
  size = "sm",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const sz = size === "sm" ? "h-4 w-4" : "h-5 w-5";

  return (
    <div ref={ref} className={`relative inline-flex items-center ${className}`} id={id}>
      <button
        type="button"
        aria-label="Help"
        onClick={() => setOpen((v) => !v)}
        className={`${sz} flex-shrink-0 rounded-full transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand ${ICON_COLORS[icon] ?? "text-blue-500"}`}
      >
        {ICONS[icon] ?? ICONS.info}
      </button>

      {open && (
        <div
          role="tooltip"
          className="absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-xl border border-border bg-surface p-3 shadow-lg"
        >
          {title && <p className="mb-1 text-xs font-semibold text-ink">{title}</p>}
          <p className="text-xs leading-relaxed text-ink-muted">{message}</p>
          <div className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r border-border bg-surface" />
        </div>
      )}
    </div>
  );
};

export default InfoTip;
